import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ChevronDownIcon, CheckCircle2Icon, LockIcon, EyeIcon, EyeOffIcon, XCircleIcon, CheckCircleIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
import { RegistrationCTAButton } from './RegistrationCTAButton';
type ForgotStep = 'recover_account' | 'verify_otp' | 'set_password' | 'success';
interface ForgotPasswordScreenProps {
  onBack: () => void;
  onComplete: () => void;
}
const ID_TYPES = ['CNIC Number', 'NICOP', 'Passport Number', 'National Tax Number'];
export function ForgotPasswordScreen({
  onBack,
  onComplete
}: ForgotPasswordScreenProps) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const [step, setStep] = useState<ForgotStep>('recover_account');
  const [direction, setDirection] = useState(1);
  // Step 1: Recover Account
  const [idType, setIdType] = useState(ID_TYPES[0]);
  const [showIdDropdown, setShowIdDropdown] = useState(false);
  const [idValue, setIdValue] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  // Step 2: OTP
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpTimer, setOtpTimer] = useState(30);
  // Step 3: Password
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Password validation
  const pwdLength = password.length >= 8 && password.length <= 16;
  const pwdUpper = /[A-Z]/.test(password);
  const pwdLower = /[a-z]/.test(password);
  const pwdNum = /[0-9]/.test(password);
  const pwdSpec = /[!@#$%^&*]/.test(password);
  const pwdValidCount = [pwdLength, pwdUpper, pwdLower, pwdNum, pwdSpec].filter(Boolean).length;
  const pwdsMatch = password.length > 0 && password === confirmPassword;
  // OTP Timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (step === 'verify_otp' && otpTimer > 0) {
      timer = setInterval(() => setOtpTimer(prev => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [step, otpTimer]);
  const navigateTo = (nextStep: ForgotStep, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  const handleBack = () => {
    if (step === 'recover_account') onBack();else if (step === 'verify_otp') navigateTo('recover_account', -1);else if (step === 'set_password') navigateTo('verify_otp', -1);
  };
  const handleOtpChange = (index: number, value: string) => {
    const digit = value.replace(/[^\d]/g, '').slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    if (digit && index < 5) {
      const nextInput = document.getElementById(`forgot-otp-${index + 1}`);
      nextInput?.focus();
    }
    if (index === 5 && digit) {
      const filled = newOtp.every(d => d !== '');
      if (filled) {
        setTimeout(() => navigateTo('set_password'), 350);
      }
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && index > 0 && !otp[index]) {
      const prev = document.getElementById(`forgot-otp-${index - 1}`);
      prev?.focus();
    }
  };
  const bgStyle = isPlatinum ? 'bg-[#1C1C1E]' : isDark ? 'bg-[#1A0E08]' : 'bg-[#FFF5F0]';
  const textStyle = isPlatinum || isDark ? 'text-white' : 'text-[#1A1A1A]';
  const subtextStyle = isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#B8A89E]' : 'text-[#5A5B5E]';
  const inputBgStyle = isPlatinum ? 'bg-[#3A3A3C]' : isDark ? 'bg-[#3D2518]' : 'bg-white';
  const inputBorderStyle = isPlatinum ? 'border-[rgba(160,160,165,0.15)]' : isDark ? 'border-[rgba(243,112,36,0.15)]' : 'border-[#F0E8E3]';
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: 'easeOut'
      }
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: 'easeIn'
      }
    })
  };
  return <div className={`w-full min-h-screen flex flex-col relative overflow-hidden ${step === 'success' ? 'bg-white' : bgStyle}`} data-id="element-887" data-mp="3mrjnj553hz3p">
      {step !== 'success' && <div className="px-6 pt-6 pb-4 flex items-center gap-3 relative z-10" data-id="element-888" data-mp="3hrnpdmj2x1w2">
          <button onClick={handleBack} className={`p-2 -ml-2 rounded-full transition-colors ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-889" data-mp="3csauqflyuqix">
            <ArrowLeftIcon className={`w-6 h-6 ${textStyle}`} strokeWidth={2} data-id="element-890" data-mp="33uqsb3cdao6q" />
          </button>
          <h2 className={textStyle} style={{
        fontFamily: 'Poppins, system-ui, sans-serif',
        fontWeight: 500,
        fontSize: 18
      }} data-id="element-891" data-mp="2yvnykf1u84e2">
            {step === 'recover_account' ? 'Recover Account' : step === 'verify_otp' ? 'Verify OTP' : 'Set New Password'}
          </h2>
        </div>}

      <div className="flex-1 px-6 flex flex-col relative z-10" data-id="element-892" data-mp="14lqyc9hbpl1p">
        <AnimatePresence mode="wait" custom={direction} data-id="element-893" data-mp="19llm445sxuij">
          {step === 'recover_account' && <motion.div key="recover_account" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="flex-1 flex flex-col pt-4" data-id="element-894" data-mp="1ecli9773k8jz">
              <div className="mb-5 relative" data-id="element-895" data-mp="1javi3v3rnxbx">
                <label className="mb-2 block" style={{
              fontFamily: 'Poppins, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 13,
              color: '#661806'
            }} data-id="element-896" data-mp="lofv6dskm7gz">
                  ID Type
                </label>
                <button onClick={() => setShowIdDropdown(!showIdDropdown)} className={`w-full relative border ${inputBorderStyle} ${inputBgStyle} flex items-center justify-between px-5`} style={{
              height: 54.5,
              borderRadius: 8
            }} data-id="element-897" data-mp="voggu6dwwzoc">
                  <span className={`text-base font-medium ${textStyle}`} data-id="element-898" data-mp="2b119d7puupz7">
                    {idType}
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 ${subtextStyle}`} data-id="element-899" data-mp="6yzyltjrpllg" />
                </button>

                <AnimatePresence data-id="element-900" data-mp="kysl1co9hfnm">
                  {showIdDropdown && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className={`absolute top-[85px] left-0 right-0 rounded-xl shadow-xl z-20 overflow-hidden border ${inputBgStyle} ${inputBorderStyle}`} data-id="element-901" data-mp="kxhkdlq44o0q">
                      {ID_TYPES.map(type => <button key={type} onClick={() => {
                  setIdType(type);
                  setShowIdDropdown(false);
                  setIdValue('');
                }} className={`w-full text-left p-4 border-b last:border-0 ${inputBorderStyle} hover:bg-black/5 ${textStyle}`} data-id="element-902" data-mp="217ywdcvsv6p">
                          {type}
                        </button>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              <div className="mb-5" data-id="element-903" data-mp="1kyupislmyb2t">
                <label className="mb-2 block" style={{
              fontFamily: 'Poppins, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 13,
              color: '#661806'
            }} data-id="element-904" data-mp="1kzw0yy6kczl2">
                  {idType}
                </label>
                <div className={`relative border ${inputBorderStyle} ${inputBgStyle} flex items-center px-5`} style={{
              height: 54.5,
              borderRadius: 8
            }} data-id="element-905" data-mp="1zwwu964el2ln">
                  <input type="text" value={idValue} onChange={e => setIdValue(e.target.value)} placeholder={`Enter ${idType}`} className={`w-full outline-none text-base bg-transparent font-medium tracking-wider ${textStyle} placeholder:opacity-40`} data-id="element-906" data-mp="1pyh5h9sum5fb" />
                </div>
              </div>

              <div className="mb-5" data-id="element-907" data-mp="jhvktv46wyis">
                <label className="mb-2 block" style={{
              fontFamily: 'Poppins, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 13,
              color: '#661806'
            }} data-id="element-908" data-mp="jiwxu54rnv35">
                  Account Number / IBAN{' '}
                  <span className="opacity-60 font-medium normal-case tracking-normal" data-id="element-909" data-mp="1167ifzxs1qh7">
                    (Optional)
                  </span>
                </label>
                <div className={`relative border ${inputBorderStyle} ${inputBgStyle} flex items-center px-5`} style={{
              height: 54.5,
              borderRadius: 8
            }} data-id="element-910" data-mp="1tvodl5cs7lh4">
                  <input type="text" value={accountNumber} onChange={e => setAccountNumber(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} placeholder="Enter account no. or IBAN" className={`w-full outline-none text-base bg-transparent font-medium tracking-wider ${textStyle} placeholder:opacity-40`} data-id="element-911" data-mp="1k5v5wffrfqi5" />
                </div>
              </div>

              <div className="mt-auto pb-8 flex justify-center" data-id="element-912" data-mp="27unutrxysy39">
                <RegistrationCTAButton onClick={() => navigateTo('verify_otp')} disabled={!idValue.trim()} data-id="element-913" data-mp="2g4lfbabhr8hr">
                  Continue
                </RegistrationCTAButton>
              </div>
            </motion.div>}

          {step === 'verify_otp' && <motion.div key="verify_otp" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="flex-1 flex flex-col pt-4" data-id="element-914" data-mp="2p36xh4d1wli0">
              <p className={`mb-8 ${subtextStyle}`} style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 400,
            fontSize: 14
          }} data-id="element-915" data-mp="2z3qx62kx9hy4">
                We've sent an OTP to your registered mobile number.
              </p>

              <div className="flex justify-between gap-2 mb-8 p-3 rounded-2xl" style={{
            background: isPlatinum ? '#2C2C2E' : isDark ? '#2D1810' : '#F9F9F9'
          }} data-id="element-916" data-mp="3cu2ssbgptyu0">
                {otp.map((digit, index) => <div key={index} className="relative group flex-1" data-id="element-917" data-mp="3ms0il3e843oq" data-mpi={index}>
                    <input id={`forgot-otp-${index}`} type="password" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleOtpKeyDown(index, e)} className={`relative w-full h-14 rounded-2xl text-center text-2xl font-bold outline-none border transition-colors ${digit ? 'border-[#F37024] bg-[#F37024]/10' : `${inputBorderStyle} ${inputBgStyle}`} ${textStyle}`} data-id="element-918" data-mp="3mq5szbs0bneb" />
                  </div>)}
              </div>

              <div className="flex flex-col items-center justify-center gap-2" data-id="element-919" data-mp="12e9nulv82ov8">
                {otpTimer > 0 ? <div className="flex items-center gap-2" data-id="element-920" data-mp="17bt0xig6ovll">
                    <span style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 400,
                fontSize: 13,
                color: '#6E6F72'
              }} data-id="element-921" data-mp="1bezgjgp6rz4q">
                      Resend OTP in{' '}
                      <span style={{
                  color: '#661806',
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-922" data-mp="3d8ov7vmrdb2r">
                        0:{String(otpTimer).padStart(2, '0')}
                      </span>
                    </span>
                  </div> : <button onClick={() => setOtpTimer(30)} className="text-[#F37024] font-bold text-sm hover:underline" data-id="element-923" data-mp="35tmnxrngjats">
                    Resend OTP
                  </button>}
              </div>

              <div className="mt-auto pb-8 pt-4 text-center" data-id="element-924" data-mp="2vvfa3hogt8w0">
                <p className={`text-[12px] ${subtextStyle}`} data-id="element-925" data-mp="3tjphydaxmqv1">
                  Do not share your OTP with anyone
                </p>
              </div>
            </motion.div>}

          {step === 'set_password' && <motion.div key="set_password" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="flex-1 flex flex-col pt-4" data-id="element-926" data-mp="3jl9lrvx1sreo">
              <div className="grid grid-cols-2 gap-2 mb-5" data-id="element-927" data-mp="287xoy4zph920">
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${pwdLength ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-928" data-mp="285cp1liyx36w">
                  8-16 Characters
                </div>
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${pwdUpper ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-929" data-mp="2alu8lpjdjbft">
                  1 Uppercase
                </div>
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${pwdLower ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-930" data-mp="1mw8a74att08v">
                  1 Lowercase
                </div>
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${pwdNum ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-931" data-mp="1cwhe4y6mad6o">
                  1 Number
                </div>
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${pwdSpec ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-932" data-mp="17zr0sim75voy">
                  1 Special Character
                </div>
              </div>

              <label className="mb-2 block" style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 500,
            fontSize: 13,
            color: '#661806'
          }} data-id="element-933" data-mp="ycenczazslk3">
                New Password
              </label>
              <div className={`relative border ${inputBorderStyle} ${inputBgStyle} flex items-center px-5 mb-5`} style={{
            height: 54.5,
            borderRadius: 8
          }} data-id="element-934" data-mp="187ygxf23385o">
                <LockIcon className="w-5 h-5 text-[#F37024] mr-3 shrink-0" data-id="element-935" data-mp="1h1l9kwazvvan" />
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter new password" className={`w-full outline-none text-base tracking-widest bg-transparent font-medium ${textStyle} placeholder:opacity-50 placeholder:text-sm placeholder:font-normal placeholder:tracking-normal`} data-id="element-936" data-mp="1d22p21n35m8c" />
                <button onClick={() => setShowPassword(!showPassword)} className="ml-2 shrink-0 p-1" data-id="element-937" data-mp="2a55smhtc4by7">
                  {showPassword ? <EyeOffIcon className={`w-5 h-5 ${subtextStyle}`} data-id="element-938" data-mp="2evdy8ofw3ig8" /> : <EyeIcon className={`w-5 h-5 ${subtextStyle}`} data-id="element-939" data-mp="2exz03dg6tydz" />}
                </button>
              </div>

              <label className="mb-2 block" style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 500,
            fontSize: 13,
            color: '#661806'
          }} data-id="element-940" data-mp="2jwtg67mrt8ue">
                Confirm New Password
              </label>
              <div className={`relative border ${confirmPassword.length > 0 && pwdsMatch ? 'border-[#22C55E]' : confirmPassword.length > 0 && !pwdsMatch ? 'border-red-500' : inputBorderStyle} ${inputBgStyle} flex items-center px-5`} style={{
            height: 54.5,
            borderRadius: 8
          }} data-id="element-941" data-mp="1w3thcjp4cyin">
                <LockIcon className="w-5 h-5 text-[#F37024] mr-3 shrink-0" data-id="element-942" data-mp="3chtfbp4eme39" />
                <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" className={`w-full outline-none text-base tracking-widest bg-transparent font-medium ${textStyle} placeholder:opacity-50 placeholder:text-sm placeholder:font-normal placeholder:tracking-normal`} data-id="element-943" data-mp="10uzng34t9ui7" />
                {confirmPassword.length > 0 && (pwdsMatch ? <CheckCircleIcon className="w-5 h-5 text-[#22C55E] shrink-0 mr-2" data-id="element-944" data-mp="i3qb7m3normx" /> : <XCircleIcon className="w-5 h-5 text-red-500 shrink-0 mr-2" data-id="element-945" data-mp="d5x66we3a3o8" />)}
                <button onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="ml-1 shrink-0 p-1" data-id="element-946" data-mp="86u0rau75vfq">
                  {showConfirmPassword ? <EyeOffIcon className={`w-5 h-5 ${subtextStyle}`} data-id="element-947" data-mp="3add0hqwtj36" /> : <EyeIcon className={`w-5 h-5 ${subtextStyle}`} data-id="element-948" data-mp="22m6aefhm0qgf" />}
                </button>
              </div>

              {confirmPassword.length > 0 && !pwdsMatch && <p className="text-xs text-red-500 font-medium mt-2 ml-2" data-id="element-949" data-mp="1xkgxsveqo4q8">
                  Passwords do not match
                </p>}

              <div className="mt-auto pb-8 pt-4 flex justify-center" data-id="element-950" data-mp="omewljw8bpa">
                <RegistrationCTAButton onClick={() => navigateTo('success')} disabled={pwdValidCount < 5 || !pwdsMatch} data-id="element-951" data-mp="pxjuop2rphz">
                  Confirm
                </RegistrationCTAButton>
              </div>
            </motion.div>}

          {step === 'success' && <motion.div key="success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="flex-1 flex flex-col items-center justify-center pt-4" data-id="element-952" data-mp="eox3tj1n7g7t">
              <div className="absolute inset-0 pointer-events-none overflow-hidden flex justify-center" data-id="element-953" data-mp="5m5tjmki6qhr">
                {/* Simple CSS Confetti */}
                {Array.from({
              length: 30
            }).map((_, i) => <motion.div key={i} className="absolute w-2 h-2 rounded-sm" style={{
              background: ['#F37024', '#22C55E', '#3B82F6', '#EAB308', '#EC4899'][i % 5],
              top: '-10%',
              left: `${Math.random() * 100}%`
            }} animate={{
              y: ['0vh', '100vh'],
              x: [`0px`, `${(Math.random() - 0.5) * 200}px`],
              rotate: [0, Math.random() * 360 + 360]
            }} transition={{
              duration: 2.5 + Math.random() * 2,
              ease: 'linear',
              repeat: Infinity,
              delay: Math.random() * 2
            }} data-id="element-954" data-mp="2qbv6tv5k648c" data-mpi={i} />)}
              </div>

              <motion.div initial={{
            scale: 0.5,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            type: 'spring',
            damping: 15
          }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-6 z-10" data-id="element-955" data-mp="1z5y25o5apukt">
                <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={2} data-id="element-956" data-mp="1ldhuga1g98dk" />
              </motion.div>

              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3 text-center z-10" style={{
            fontFamily: 'Poppins, system-ui, sans-serif'
          }} data-id="element-957" data-mp="1gex0gscsjkxk">
                Password Changed Successfully!
              </h2>

              <p className="text-[#5A5B5E] text-center mb-10 max-w-[280px] z-10" style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontSize: 14
          }} data-id="element-958" data-mp="14ub10l2akhme">
                Your password has been updated. You can now log in with your new
                password.
              </p>

              <div className="w-full mt-auto pb-8 z-10 flex justify-center" data-id="element-959" data-mp="1itswfja3et4q">
                <RegistrationCTAButton onClick={onComplete} data-id="element-960" data-mp="1irpuc0vo19jn">
                  Go to Login
                </RegistrationCTAButton>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}